import React from "react";

import Box from "@busbud/design-system-components/dist/Box";
import Stack from "@busbud/design-system-components/dist/Stack";
import Text from "@busbud/design-system-components/dist/Text";

import { Markdown } from "@app/components/markdown";
import { useAppContext } from "@app/helpers/hooks";
import { isOperatorNameViaRail } from "@app/helpers/operators";
import { Money } from "@app/lib/money";
import { getRefundableAmount } from "@app/modules/account/components/trip-details/helpers";
import { HepstarBannerChip } from "@app/modules/account/components/trip-details/hepstar-banner";
import type { TranslatorService } from "@app/services/translator";
import type { NapiCancellationOption } from "@app/types/napi-v2-cancellation-types";

import { useStyles } from "./cancel-message-styles";
import { getRefundMethod } from "./helpers";
import { CancelTripAlerts } from "../cancel-trip-alerts";
import { useCancelTripContext } from "../cancel-trip-context";

const BOX_PROPS = {
  p: {
    xs: 1,
    md: 2
  }
};

interface Props {
  cancellation_options: NapiCancellationOption[];
  operator_name: string;
}

export const CancelMessage: React.FC<Props> = ({
  cancellation_options,
  operator_name
}) => {
  const { translator, browser_locale } = useAppContext();
  const { display_hepstar_banner } = useCancelTripContext();
  const { classes } = useStyles();

  const banner_message = `${translator.t(
    "!ancillary.hepstar-banner.after-operator-cancel.body"
  )} ${translator.t("!ancillary.hepstar-banner.support-evidence.body")}`;
  const { additional_refund_text, refund_text } = getText(
    translator,
    browser_locale,
    cancellation_options,
    operator_name
  );

  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="center"
      className={classes.container}
    >
      <CancelTripAlerts />
      <Text
        component="span"
        align="center"
        size="lg"
        color="text.primary.opaque"
      >
        <Box {...BOX_PROPS}>
          <Markdown source={refund_text} />
          <br />
          <Markdown source={additional_refund_text} />
        </Box>
        {!!display_hepstar_banner && (
          <Box pt={2}>
            <HepstarBannerChip
              is_displayed={display_hepstar_banner}
              message={banner_message}
            />
          </Box>
        )}
      </Text>
    </Stack>
  );
};

function getText(
  translator: TranslatorService,
  browser_locale: string,
  cancellation_options: NapiCancellationOption[],
  operator_name: string
) {
  const refund_method = getRefundMethod(cancellation_options);
  const { refundable_amount, currency_code } =
    getRefundableAmount(cancellation_options);
  const refundable_amount_price = new Money(refundable_amount).getPrice(
    currency_code,
    browser_locale
  );

  const additional_refund_text = isOperatorNameViaRail(operator_name)
    ? translator.t(
        "!account.trip-details.cancel-tickets-modal.may-refund-additional-text"
      )
    : translator.t(
        "!account.trip-details.cancel-tickets-modal.refund-additional-text"
      );

  if (refund_method === "voucher") {
    return {
      refund_text: translator.t(
        "!account.trip-details.cancel-tickets-modal.refund-as-voucher-text",
        {
          operator: operator_name
        }
      ),
      additional_refund_text: translator.t(
        "!account.trip-details.cancel-tickets-modal.refund-as-voucher-additional-text"
      )
    };
  } else if (refund_method === "busbud_credits") {
    return {
      refund_text: translator.t(
        "!account.trip-details.cancel-tickets-modal.refund-as-busbud-credits-text",
        {
          amount: refundable_amount_price
        }
      ),
      additional_refund_text: translator.t(
        "!account.trip-details.cancel-tickets-modal.refund-as-busbud-credits-additional-text"
      )
    };
  } else {
    return {
      refund_text: translator.t(
        "!account.trip-details.cancel-tickets-modal.refund-text",
        {
          amount: refundable_amount_price
        }
      ),
      additional_refund_text: additional_refund_text
    };
  }
}
